// ─── CollapsibleSection — extracted from AdminCCFSLeads.jsx (Phase 1) ───
// Layout wrapper used across the file. Identical behaviour, no API/business logic.

import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, FolderOpen } from 'lucide-react';

export function CollapsibleSection({
  icon: Icon, title, borderColor, iconColor, bgHover, chevronBg,
  children, defaultOpen = false, testId, badge, summary, folderUrl,
  headerRight, sectionKey, activeSection, setActiveSection,
}) {
  const effectiveKey = sectionKey || testId;
  const controlled = !!effectiveKey && !!setActiveSection;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlled ? activeSection === effectiveKey : uncontrolledOpen;
  const toggle = () => {
    if (controlled) setActiveSection(open ? null : effectiveKey);
    else setUncontrolledOpen(!uncontrolledOpen);
  };
  // On mount, honour defaultOpen for controlled mode too (first defaultOpen section wins)
  useEffect(() => {
    if (controlled && defaultOpen) {
      setActiveSection(prev => prev == null ? effectiveKey : prev);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className={`bg-white rounded-lg border ${borderColor || 'border-slate-200'} overflow-hidden`} data-testid={testId}>
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={e => e.key === 'Enter' && toggle()}
        className={`w-full flex items-center justify-between px-4 py-3 ${bgHover || 'hover:bg-slate-50/50'} transition-colors cursor-pointer`}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className={`w-4 h-4 shrink-0 ${iconColor || 'text-slate-500'}`} />}
          <span className={`text-xs font-semibold uppercase tracking-wide ${iconColor || 'text-slate-500'}`}>{title}</span>
          {badge}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!open && summary && <span className="text-[10px] text-slate-400 max-w-[320px] truncate hidden md:inline">{summary}</span>}
          {folderUrl && (
            <a href={folderUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="w-7 h-7 rounded-full flex items-center justify-center bg-amber-50 hover:bg-amber-100 text-amber-500 hover:text-amber-700 transition-colors" title="Open Document Folder" data-testid={testId ? `${testId}-folder` : undefined}>
              <FolderOpen className="w-3.5 h-3.5" />
            </a>
          )}
          {headerRight && <span onClick={e => e.stopPropagation()}>{headerRight}</span>}
          <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${open ? (chevronBg || 'bg-slate-200') : 'bg-slate-100 hover:bg-slate-200'}`}>
            {open ? <ChevronUp className={`w-4 h-4 ${iconColor || 'text-slate-500'}`} /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </div>
      {open && <div className="px-4 pb-4 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
}
